import styles from "./index.module.css";

import React, {useState, useEffect} from "react";
import {components} from "react-select";
import {getDate, getDateString} from 'Core/helpers/datetime';
import {getAppDatePluginLocale} from 'Core/helpers/locale';
import Button, {BUTTON_DISPLAY_TYPE, BUTTON_STYLE} from "Core/components/display/Button";
import {get, omit, map} from "lodash";
import {getDaysInMonth} from "date-fns";
import {getArray, getBoolean, getString} from 'Core/helpers/data';
import {getTranslation, i18nEffect, translate} from 'Core/i18n';

/**
 * Select option replacement component using Label component
 * @description Use this as an Option component replacement for the SelectInput (react-select) component. You can pass
 * Label component props through 'labelProps' prop of the SelectInput component. These prop can be an instance of
 * SelectDynamicValueFunction class if prop should depend on option data.
 *
 * @param {Object} props - Option props sent by the react-select component to all the options.
 * @return {JSX.Element}
 * @constructor
 */
const YearDaySelectInputMenuComponent = props => {
	// Define component's translation path.
	// NOTE: This is not necessary but is a good practice when component does not use some global translation path.
	const translationPath = 'YearDaySelectInput';
	
	// Use 'i18nEffect' function in React 'useEffect' function to update (re-render) component when translation changes.
	const i18nEffectHook = i18nEffect.bind(null, useState(getTranslation())[1]);
	useEffect(() => { i18nEffectHook(); }, [i18nEffectHook]);
	
	const {isMulti, selectOption, getValue} = props;
	const menuProps = get(props, 'selectProps.menuProps', {});
	const selectId = get(props, 'selectProps.id', '');
	const valueFormat = getString(menuProps, 'valueFormat');
	const isLeapYear = getBoolean(menuProps, 'isLeapYear');
	const year = (isLeapYear ? '2024' : '2025');
	const [month, setMonth] = useState();
	const [day, setDay] = useState();

	/**
	 * Check if a given date is selected
	 * @param {number} month
	 * @param {number} day
	 * @return {boolean}
	 */
	const isSelected = (month, day) => {
		if (!month || !day) return false;
		
		const values = map(getArray(getValue()), 'value');
		return values.includes(
			getDateString(getDate(`${year}-${month}-${day}`, 'yyyy-M-d'), valueFormat, getAppDatePluginLocale())
		);
	};

	/**
	 * Check if today is selected
	 * @return {boolean}
	 */
	const isTodaySelected = () => {
		const today = new Date();
		return isSelected(today.getMonth() + 1, today.getDate());
	}

	/**
	 * Get data to return as a selected option
	 * @param {number} month - Selected month.
	 * @param {number} day - Selected day.
	 * @return {{__isNew__: true, label: string, value: string}}
	 */
	const getDataToReturn = (month, day) => ({
		__isNew__: true,
		label: getDateString(getDate(`${year}-${month}-${day}`, 'yyyy-M-d'), valueFormat, getAppDatePluginLocale()),
		value: getDateString(getDate(`${year}-${month}-${day}`, 'yyyy-M-d'), valueFormat, getAppDatePluginLocale()),
	});
	
	return (
		<components.Menu
			className={`${props.className ? props.className : ''} ${styles['wrapper']}`} 
			{...omit(props, 'className')}
		>
			<div id={`${selectId}-menu`} className="select-input__menu-list">
				<div className={styles['months']}>
					{[...Array(12).keys()].map(i => i + 1).map(m =>
						<Button
							key={m}
							className={
								`${styles['btn']} ${styles['monthBtn']} ${isSelected(m, day) ? styles['selected'] : ''}` +
								` ${isMulti ? styles['isMulti'] : ''}`
							}
							label={getDateString(getDate(`${m}`, 'M'), 'MMM', getAppDatePluginLocale())}
							onClick={() => {
								if (day) {
									if (day <= getDaysInMonth(getDate(`${year}-${m}`, 'yyyy-M'))) {
										selectOption(getDataToReturn(m, day))
									} else {
										setMonth(m);
										setDay(undefined);
									}
								} else {
									setMonth(m);
								}
							}}
							displayStyle={month===m || isSelected(m,day) ? BUTTON_STYLE.ACTION : BUTTON_STYLE.DEFAULT}
							displayType={month===m || isSelected(m,day) ? BUTTON_DISPLAY_TYPE.SOLID : BUTTON_DISPLAY_TYPE.NONE}
							title={isMulti && isSelected(m, day) ? translate('already_selected_tooltip', translationPath) : ''}
						/>
					)}
				</div>
				<div className={styles['days']}>
					{[...Array((
						month ? getDaysInMonth(getDate(`${year}-${month}`, 'yyyy-M')) : 31
					)).keys()].map(i => i + 1).map(d =>
						<Button
							key={d}
							className={
								`${styles['btn']} ${styles['dayBtn']} ${isSelected(month, d) ? styles['selected'] : ''}` +
								` ${isMulti ? styles['isMulti'] : ''}`
							}
							label={`${d}`}
							onClick={() => {
								if (month) selectOption(getDataToReturn(month, d));
								else setDay(d);
							}}
							displayStyle={day===d || isSelected(month,d) ? BUTTON_STYLE.ACTION : BUTTON_STYLE.DEFAULT}
							displayType={day===d || isSelected(month,d) ? BUTTON_DISPLAY_TYPE.SOLID : BUTTON_DISPLAY_TYPE.NONE}
							title={isMulti && isSelected(month,d) ? translate('already_selected_tooltip',translationPath) : ''}
						/>
					)}
				</div>
				<div className={styles['today']}>
					<Button
						className={
							`${styles['btn']} ${styles['todayBtn']} ${isTodaySelected() ? styles['selected'] : ''}` +
							` ${isMulti ? styles['isMulti'] : ''}`
						}
						label={translate('Today', translationPath)}
						displayType={isTodaySelected() ? BUTTON_DISPLAY_TYPE.SOLID : BUTTON_DISPLAY_TYPE.NONE}
						displayStyle={isTodaySelected() ? BUTTON_STYLE.ACTION : BUTTON_STYLE.DEFAULT}
						onClick={() => {
							const todayValue = getDateString(new Date(), valueFormat, getAppDatePluginLocale());
							selectOption({__isNew__: true, label: todayValue, value: todayValue})
						}}
						title={isMulti && isTodaySelected() ? translate('already_selected_tooltip', translationPath) : ''}
					/>
				</div>
			</div>
		</components.Menu>
	)
};

export default YearDaySelectInputMenuComponent;